import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AdminSanctionDto,
  adminApplySanction,
  adminSanctionPreview,
  BrandSanctionLevel,
  SanctionPreview,
} from '../../api/payment/brand-sanction.api';
import { apiErrorMessage } from '../../utils/api-error-message';
import { DECISION_REASON_LIMITS } from '../../utils/brand-dispute';
import { isLowerLevel, sanctionLevelLabel, sanctionWindowLabel, SANCTION_LEVELS } from '../../utils/brand-sanction';
import {
  buildApplyPayload,
  compensationNeed,
  DEFAULT_COMPENSATION_REASON,
  SanctionFormState,
  validateSanctionForm,
} from '../../utils/brand-sanction-admin';
import { AdminOrdererLabel } from '../brand-deal/orderer-badge';
import AdminSanctionHistory from './admin-sanction-history';
import AdminTotpInput from './admin-totp-input';

const INPUT =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white';

interface Props {
  adAccountId: string;
  /** Có khi phạt ngay sau khi quyết kháng cáo (lưu kèm để tra ngược). */
  disputeId?: string | null;
  onDone: (sanction: AdminSanctionDto) => void;
  onCancel?: () => void;
  cancelLabel?: string;
}

const emptyForm = (level: BrandSanctionLevel): SanctionFormState => ({
  level,
  reason: '',
  lowerLevelReason: '',
  compensations: [],
});

/**
 * Form xử phạt người đặt đơn (spec §4.2, §4.3): xem số lần bị phạt 12 tháng, cấp gợi ý (chọn sẵn), lý do, lý do hạ cấp
 * khi chọn nhẹ hơn, bồi thường từng creator kèm tổng cần trừ so với số dư, mã Authenticator. Dùng ở tab "Xử phạt người
 * đặt đơn" và ở bước sau khi quyết kháng cáo "Creator đúng".
 */
const AdminSanctionForm: React.FC<Props> = ({ adAccountId, disputeId, onDone, onCancel, cancelLabel = 'Huỷ' }) => {
  const [preview, setPreview] = useState<SanctionPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<SanctionFormState>(emptyForm('Warning'));
  const [pickCreatorId, setPickCreatorId] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);

  const loadPreview = useCallback(
    (resetForm: boolean) => {
      setLoadError(null);
      adminSanctionPreview(adAccountId)
        .then((p) => {
          setPreview(p);
          if (resetForm) setForm(emptyForm(p.suggestedLevel));
        })
        .catch((e) => setLoadError(apiErrorMessage(e, 'Không tải được thông tin người đặt đơn.')));
    },
    [adAccountId]
  );

  useEffect(() => {
    loadPreview(true);
  }, [loadPreview]);

  // Khi chưa dựng được form (đang tải hoặc lỗi) vẫn phải có đường thoát, nếu không admin kẹt trong hộp thoại.
  const leaveButton = onCancel && (
    <button type="button" onClick={onCancel} className="mt-3 rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-slate-600 dark:text-white">
      {cancelLabel}
    </button>
  );
  if (loadError)
    return (
      <div>
        <p className="text-sm text-rose-600">{loadError}</p>
        {leaveButton}
      </div>
    );
  if (!preview)
    return (
      <div>
        <p className="text-sm text-gray-500">Đang tải…</p>
        {leaveButton}
      </div>
    );

  const lower = isLowerLevel(form.level, preview.suggestedLevel);
  const error = validateSanctionForm(form, preview);
  const need = compensationNeed(preview, form.compensations);
  const creatorName = (id: string) => preview.creators.find((c) => c.creatorId === id)?.displayName ?? id.slice(0, 8);
  const addable = preview.creators.filter((c) => !form.compensations.some((l) => l.creatorId === c.creatorId));

  const addCreator = () => {
    const id = pickCreatorId || addable[0]?.creatorId;
    if (!id) return;
    setForm((f) => ({ ...f, compensations: [...f.compensations, { creatorId: id, amountVnd: 0, reason: DEFAULT_COMPENSATION_REASON }] }));
    setPickCreatorId('');
  };

  const updateLine = (creatorId: string, patch: Partial<{ amountVnd: number; reason: string }>) =>
    setForm((f) => ({
      ...f,
      compensations: f.compensations.map((l) => (l.creatorId === creatorId ? { ...l, ...patch } : l)),
    }));

  const removeLine = (creatorId: string) =>
    setForm((f) => ({ ...f, compensations: f.compensations.filter((l) => l.creatorId !== creatorId) }));

  const submit = async () => {
    if (error || totp.length !== 6) return;
    try {
      setBusy(true);
      const saved = await adminApplySanction(buildApplyPayload(adAccountId, disputeId, form, preview), totp);
      toast.success('Đã xử phạt người đặt đơn.');
      onDone(saved);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Xử phạt thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40">
        <div className="font-semibold text-slate-800 dark:text-white">
          <AdminOrdererLabel name={preview.businessName} kind={preview.ordererKind} ownerIdentityName={preview.ownerIdentityName} />
        </div>
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
          Bị phạt {preview.countLast12Months} lần trong 12 tháng gần nhất (tính mọi tài khoản cùng chủ sở hữu hoặc cùng CCCD).
        </div>
        {preview.active && (
          <div className="mt-1 text-xs font-semibold text-rose-600 dark:text-rose-300">
            Đang chịu: {sanctionLevelLabel(preview.active.level)} ({sanctionWindowLabel(preview.active)})
          </div>
        )}
      </div>

      <fieldset>
        <legend className="mb-1 font-medium text-slate-700 dark:text-slate-200">Mức xử phạt</legend>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {SANCTION_LEVELS.map((l) => (
            <label
              key={l}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${
                form.level === l ? 'border-[#0057FF] bg-blue-50 dark:bg-slate-700' : 'border-gray-300 dark:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="sanction-level"
                checked={form.level === l}
                onChange={() => setForm((f) => ({ ...f, level: l }))}
              />
              <span className="text-slate-800 dark:text-white">
                {sanctionLevelLabel(l)}
                {l === preview.suggestedLevel ? ' (gợi ý)' : ''}
              </span>
            </label>
          ))}
        </div>
        {form.level === 'Permanent' && (
          <p className="mt-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
            Khoá vĩnh viễn đóng tin đăng đang mở và hoàn suất chưa ai nhận, chặn quảng cáo doanh nghiệp và nạp ví doanh nghiệp, đồng
            thời đưa mã số thuế và CCCD của chủ tài khoản vào danh sách đen.
          </p>
        )}
      </fieldset>

      {lower && (
        <label className="block font-medium text-slate-700 dark:text-slate-200">
          Lý do chọn mức nhẹ hơn gợi ý (10–500 ký tự) <span className="text-red-500">*</span>
          <textarea
            aria-label="Lý do chọn mức nhẹ hơn gợi ý"
            value={form.lowerLevelReason}
            onChange={(e) => setForm((f) => ({ ...f, lowerLevelReason: e.target.value }))}
            maxLength={500}
            rows={2}
            className={`${INPUT} resize-none`}
          />
        </label>
      )}

      <label className="block font-medium text-slate-700 dark:text-slate-200">
        Lý do xử phạt ({DECISION_REASON_LIMITS.minLength}–{DECISION_REASON_LIMITS.maxLength} ký tự){' '}
        <span className="text-red-500">*</span>
        <textarea
          aria-label="Lý do xử phạt"
          value={form.reason}
          onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
          maxLength={DECISION_REASON_LIMITS.maxLength}
          rows={3}
          placeholder="VD: từ chối bài đúng yêu cầu ba lần trong tháng để lấy lại tiền ký quỹ."
          className={`${INPUT} resize-none`}
        />
      </label>

      <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
        <div className="font-medium text-slate-700 dark:text-slate-200">Bồi thường cho creator (không bắt buộc)</div>
        {preview.creators.length === 0 ? (
          <p className="text-xs text-gray-500">Người này chưa có đơn nào với creator.</p>
        ) : (
          <>
            {form.compensations.map((l) => (
              <div key={l.creatorId} data-testid="compensation-line" className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px_1fr_auto]">
                <span className="self-center font-medium text-slate-800 dark:text-white">{creatorName(l.creatorId)}</span>
                <input
                  aria-label={`Số tiền bồi thường cho ${creatorName(l.creatorId)}`}
                  type="number"
                  min={0}
                  value={l.amountVnd || ''}
                  onChange={(e) => updateLine(l.creatorId, { amountVnd: Math.trunc(Number(e.target.value) || 0) })}
                  placeholder="Số tiền (đ)"
                  className={INPUT}
                />
                <input
                  aria-label={`Lý do bồi thường cho ${creatorName(l.creatorId)}`}
                  value={l.reason}
                  maxLength={500}
                  onChange={(e) => updateLine(l.creatorId, { reason: e.target.value })}
                  className={INPUT}
                />
                <button type="button" onClick={() => removeLine(l.creatorId)} className="self-center text-xs font-semibold text-rose-600">
                  Bỏ
                </button>
              </div>
            ))}
            {addable.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Chọn creator"
                  value={pickCreatorId}
                  onChange={(e) => setPickCreatorId(e.target.value)}
                  className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                >
                  {addable.map((c) => (
                    <option key={c.creatorId} value={c.creatorId}>
                      {c.displayName} ({c.dealCount} đơn)
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addCreator}
                  className="rounded border border-[#0057FF] px-2.5 py-1 text-xs font-semibold text-[#0057FF]"
                >
                  Thêm creator
                </button>
              </div>
            )}
            {form.compensations.length > 0 && (
              <p className={`text-xs ${need.short ? 'font-semibold text-rose-600' : 'text-gray-600 dark:text-gray-300'}`}>
                {need.label}
                {preview.ordererKind === 'Individual' ? ` (tỉ giá ${preview.vndPerCa}đ một Ca)` : ''}. Creator nhận số tiền sau
                phí sàn 10%.
              </p>
            )}
          </>
        )}
      </div>

      <AdminTotpInput value={totp} onChange={setTotp} />

      {error && <p className="text-xs font-medium text-rose-600">{error}</p>}

      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="h-10 min-w-[90px] rounded-md border border-gray-300 px-4 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            {cancelLabel}
          </button>
        )}
        <button
          type="button"
          disabled={!!error || totp.length !== 6 || busy}
          onClick={submit}
          className="h-10 min-w-[130px] rounded-md bg-rose-600 px-4 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Đang xử lý…' : 'Xử phạt'}
        </button>
      </div>

      <div>
        <div className="mb-1 font-medium text-slate-700 dark:text-slate-200">Lịch sử xử phạt</div>
        <AdminSanctionHistory items={preview.history} onChanged={() => loadPreview(false)} />
      </div>
    </div>
  );
};

export default AdminSanctionForm;
